import { FC } from 'react';

import { EAssetType } from 'constants/enums';
import { DEFAULT_BACKGROUND_COLOR, GRADIENT_INPUT_STYLE } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignFormikProps } from 'views/OfferDesign/OfferDesign.types';
import { getFieldError, getInputErrorProps, handleButtonSuffixUpload } from 'views/OfferDesign/OfferDesign.utils';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

type BundleFormFieldsProps = OfferDesignFormikProps & {
  isNameUnique: boolean;
  isExternalIdUnique: boolean;
};

export const BundleFormFields: FC<BundleFormFieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique
}) => {
  const uniqueFlags = { isNameUnique, isExternalIdUnique };
  const nameError = getFieldError('name', touched, errors, uniqueFlags, 'isNameUnique');
  const externalIdError = getFieldError('externalId', touched, errors, uniqueFlags, 'isExternalIdUnique');
  const backgroundImageError = touched.backgroundImage ? errors.backgroundImage : undefined;

  return (
    <div className="rounded-lg border border-base-border p-9">
      <h3 className="text-[20px] font-semibold text-zinc-900">Bundle</h3>

      <div className="flex gap-5 mt-4">
        <Input
          label="Name"
          isRequired
          name="name"
          placeholder="Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(nameError)}
          className="flex-1"
        />
        <Input
          label="External ID"
          isRequired
          name="externalId"
          placeholder="External ID"
          value={values.externalId}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(externalIdError)}
          className="flex-1"
        />
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">
        Bundle Design
      </h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Customize the bundle to fit your store theme.
      </p>

      {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
      <div className={`w-1/2 ${GRADIENT_INPUT_STYLE}`}>
        <AcGradientInput
          header="Background Color"
          name="backgroundColor"
          defaultValue={typeof values.backgroundColor === 'object' && values.backgroundColor
            ? values.backgroundColor
            : { colorOne: (values.backgroundColor as string) || DEFAULT_BACKGROUND_COLOR }}
          onChange={handleChange}
          setValue={setFieldValue}
          onBlur={handleBlur}
        />
      </div>

      <div className="mt-5">
        <UploadImage
          title="Background Image"
          isRequired
          className="w-full !flex flex-row px-5"
          onUpload={(url) => setFieldValue('backgroundImage', url)}
          selectedImageUrl={values.backgroundImage}
          forceType={EAssetType.BG_BUNDLE}
          hasError={!!backgroundImageError}
          errorMessage={backgroundImageError}
          TooltipContent={() => <span>Upload a background image for the bundle offer card</span>}
        />
      </div>

      <div className="mt-5">
        <UploadImage
          title="Button Suffix Badge"
          className="w-full !flex flex-row px-5"
          onUpload={(url, name, mimeType) => handleButtonSuffixUpload(setFieldValue, url, name, mimeType)}
          selectedImageUrl={values.buttonSuffixImage || values.buttonSuffixAnimation}
          forceType={EAssetType.BUTTON_ASSET_SUFFIX}
          TooltipContent={() => <span>Upload a button suffix badge image or animation</span>}
        />
      </div>
    </div>
  );
};
